import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";

import {
  PageLoader,
  flukeLightTheme,
  ThemeProvider,
} from "@flukecorp-emu/common-ui";
import { Provider } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import { thunk } from "redux-thunk";
import RootReducer from "./reducers";
import { getMe } from "./services/auth";

const queryClient = new QueryClient();
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: {
    queryClient,
    auth: {
      isAuthenticated: false,
      user: null,
    },
  },
});

const preloadState = {};

const store = configureStore({
  reducer: RootReducer,
  middleware: () => [thunk],
  preloadedState: preloadState,
});

const RouteWithAuth = () => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    getMe()
      .then((userData) => {
        if (userData) {
          setIsAuthenticated(true);
          setUser(userData);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      })
      .catch(() => {
        setIsAuthenticated(false);
        setUser(null);
      })
      .finally(() => {
        setIsCheckingAuth(false);
      });
  }, []);

  if (isCheckingAuth) {
    return <PageLoader open={isCheckingAuth} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider
        router={router}
        context={{
          queryClient,
          auth: {
            isAuthenticated,
            user,
          },
        }}
      />
    </QueryClientProvider>
  );
};

const App = () => {
  return (
    <StrictMode>
      <Provider store={store}>
        <ThemeProvider theme={flukeLightTheme}>
          <RouteWithAuth />
        </ThemeProvider>
      </Provider>
    </StrictMode>
  );
};

const root = createRoot(document.getElementById("root"));

root.render(<App />);
